import { isRTL } from "@app/lib/helper";
import { cn } from "@app/lib/utils";
import type { Project } from "@modules/investor-relations/data";
import { getProjectSectors } from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import { MapPin } from "lucide-react";

/**
 * Badge tint per sector, following the brand sector colors already used by the
 * About Us subsidiaries section. Construction's `#FFB624` is too light to read
 * as text on white, so only its chip keeps the brand hue and the label is
 * darkened.
 */
const SECTOR_BADGE_CLASSES: Record<string, string> = {
  hvac: "bg-aqua-blue/10 text-aqua-blue",
  steel: "bg-steel/10 text-steel",
  insulation: "bg-insulation/10 text-insulation",
  construction: "bg-construction/20 text-[#8A5B00]",
};

export function ProjectCard({
  project,
  index,
}: {
  project: Project;
  /** Drives the cascade when a tab switch remounts the cards. */
  index: number;
}) {
  const sectors = getProjectSectors(project.key);
  const hasLocation = Boolean(project.city && project.country);

  return (
    <article
      style={{ animationDelay: `${index * 60}ms` }}
      className="flex h-full animate-fade flex-col overflow-hidden rounded-2xl border border-mercury bg-white shadow-leadership-card animate-duration-500 animate-fill-both"
    >
      <img
        src={project.image}
        alt={trans(project.name)}
        width={900}
        height={675}
        loading="lazy"
        className="aspect-[4/3] w-full object-cover"
      />

      {/* Image + sector badges + name + city + country only — the approved
          content sheet explicitly excludes descriptions, values, clients,
          dates and scope. */}
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        {sectors.length > 0 && (
          <ul className="flex flex-wrap items-center gap-1.5">
            {sectors.map(({ key, label }) => (
              <li
                key={key}
                className={cn(
                  "rounded-md px-2 py-0.5 text-[0.6875rem] font-semibold leading-5",
                  SECTOR_BADGE_CLASSES[key],
                )}
              >
                {trans(label)}
              </li>
            ))}
          </ul>
        )}

        <h3 className="text-sm font-bold leading-snug text-marine sm:text-base">
          {trans(project.name)}
        </h3>

        {/* Every project in the client's bilingual sheet carries a city and
            country; the guard only covers a future entry that does not. The
            names run from one to three lines, so the line is pushed to the
            bottom to keep it level across a row of cards. */}
        {hasLocation && (
          <p className="mt-auto flex items-start gap-1 pt-1 text-xs text-marine/55 sm:text-[0.8125rem]">
            <MapPin className="mt-px size-3.5 shrink-0 text-bright-orange" />
            {/* Arabic separates the two with its own comma, as the client's
                bilingual sheet writes it. */}
            <span className="leading-snug">
              {trans(project.city!)}
              {isRTL() ? "، " : ", "}
              {trans(project.country!)}
            </span>
          </p>
        )}
      </div>
    </article>
  );
}
